import { useGoogleLogin } from '@react-oauth/google';
import { useCommonTracking } from 'common-tracking';
import {
  Button,
  css,
  Spacer,
  styled,
  Text,
  theme,
  useToaster,
} from 'design-system';
import { useState } from 'react';

import { useAuthContext } from '../auth-context';
import { AUTH_TOKEN_KEY } from '../constants';
import { useGoogleSignIn } from '../google-signin';

export const SocialLoginButtons = ({
  onSignIn,
  onSignUp,
  checkFirstTimeUse,
}: {
  onSignIn?: () => void;
  onSignUp?: (token: string) => void | undefined;
  checkFirstTimeUse?: (token: string) => void;
}) => {
  const {
    authState,
    editage,
    listeners: { onUserCreated },
  } = useAuthContext();
  const { trackCommonEvent } = useCommonTracking();
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(false);

  const { googleLogIn } = useGoogleSignIn({
    baseUrl: editage.baseUrl,
    product: editage.product,
    useCustomRlifeSocialAuth: true,
    on: {
      loggedIn: async (token, ssoUserId, isExistingUser) => {
        if (checkFirstTimeUse) {
          await checkFirstTimeUse(token);
        }
        if (isExistingUser === 'true') {
          trackCommonEvent({
            type: 'login-in',
            userId: ssoUserId,
            authChannel: 'google',
          });
          await onSignIn?.();
        } else {
          if (onSignUp) {
            await onSignUp?.(token);
          } else {
            await onSignIn?.();
          }
          console.info(`Event called | sign-up | ${ssoUserId}`);
          trackCommonEvent({
            type: 'sign-up',
            userId: ssoUserId,
            success: true,
            authChannel: 'google',
          });
        }
        onUserCreated(token);
        authState.invalidate();
        setLoading(false);
      },
      tokenObtained: (token, ssoUserId) => {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem('ssoUserId', ssoUserId.toString());
      },
      error: (errorVal) => {
        setLoading(false);
        showToast({ color: 'danger', title: errorVal });
      },
    },
  });

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      googleLogIn({ gAccessToken: tokenResponse.access_token });
    },
    onError: () => {
      setLoading(false);
      showToast({ color: 'danger', title: '' });
    },
  });

  return (
    <>
      <OutlineButton
        loading={
          loading ||
          authState.name === 'checking if logged in' ||
          authState.name === 'logging in'
        }
        onClick={() => {
          googleLogin();
        }}
      >
        <img src="/assets/social-icons/icon-google.svg" />
        <GoogleText>Continue with Google</GoogleText>
      </OutlineButton>
      <Spacer height={20} />
      <SectionSeparator>
        <Text className={textGray()}>or continue with email</Text>
        <Line />
      </SectionSeparator>
      <Spacer height={20} />
    </>
  );
};

const OutlineButton = styled(Button, {
  background: theme.colors.white,
  width: '100%',
  border: `1px solid ${theme.colors.gray8}`,
  color: theme.colors.gray11,
  fontWeight: 500,
  fontSize: 16,
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
});

const SectionSeparator = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
});

const Line = styled('div', {
  width: '100%',
  height: '1px',
  background: theme.colors.gray8,
  position: 'absolute',
  top: '13px',
  zIndex: -1,
});

const GoogleText = styled('span', {});

const textGray = css({
  textAlign: 'center',
  padding: '0 13px',
  background: theme.colors.white,
  color: theme.colors.gray11,
});
